// src/components/navigation/AppShell.js
//
// Phase 3 — Navigation Architecture: Step 7 reusable navigation component.
//
// The authenticated app frame: persistent desktop LeftNav, mobile TopNav,
// mobile bottom nav, and the main content column. Wraps its children in
// `NavigationProvider` (Step 4 context-switching state). This is the single
// shell used for every authenticated page — pages must not build their own
// navigation chrome.

import React from "react";
import { Box } from "@mui/material";
import LeftNav from "../LeftNav";
import TopNav from "./TopNav";
import MobileBottomNav from "../MobileBottomNav";
import { NavigationProvider } from "../../contexts/NavigationContext";
import { APP_CONTENT_MAX_WIDTH } from "../../layout/constants";

/**
 * @param {object} props
 * @param {object|null} props.user
 * @param {() => void} props.onLogout
 * @param {boolean} props.standalone - Render public/auth content without app chrome.
 * @param {React.ReactNode} props.children - routed page content
 */
export default function AppShell({ user, onLogout, standalone = false, supportAction, children }) {
  if (standalone) {
    return <Box sx={{ width: "100%" }}>{supportAction && <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1 }}>{supportAction}</Box>}{children}</Box>;
  }

  return (
    <NavigationProvider>
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
        {user ? (
          <Box
            component="a"
            href="#main-content"
            sx={{
              position: "fixed",
              top: 8,
              left: 8,
              zIndex: 2000,
              minHeight: 44,
              px: 2,
              display: "flex",
              alignItems: "center",
              borderRadius: 1,
              bgcolor: "background.paper",
              color: "primary.dark",
              boxShadow: 3,
              textDecoration: "none",
              transform: "translateY(-160%)",
              transition: "transform 120ms ease-out",
              "&:focus": { transform: "translateY(0)", outline: "3px solid", outlineColor: "primary.main" },
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            Skip to main content
          </Box>
        ) : null}

        {/* Persistent left nav — desktop only, authenticated users */}
        {user && (
          <Box sx={{ display: { xs: "none", md: "block" }, flexShrink: 0 }}>
            <LeftNav user={user} onLogout={onLogout} />
          </Box>
        )}

        {/* Main column */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {/* Authenticated mobile top bar. Public/auth pages own their chrome. */}
          {user ? <TopNav user={user} onLogout={onLogout} supportAction={supportAction} /> : null}

          {/* Page content */}
          <Box
            component={user ? "main" : "div"}
            id={user ? "main-content" : undefined}
            tabIndex={user ? -1 : undefined}
            sx={
              user
                ? {
                    flex: 1,
                    pt: { xs: 1, md: 2 },
                    pb: { xs: "72px", md: 0 }, // room for mobile bottom nav
                  }
                : {
                    width: "100%",
                    maxWidth: APP_CONTENT_MAX_WIDTH,
                    mx: "auto",
                  }
            }
          >
            {children}
          </Box>
        </Box>

        {/* Mobile bottom nav */}
        {user && (
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <MobileBottomNav />
          </Box>
        )}
      </Box>
    </NavigationProvider>
  );
}
